import MuiStack, { StackProps as MuiStackProps } from "@mui/material/Stack";
import React from "react";

import { ResponsiveValue, multiplySpacing } from "../../theme/grid";

export interface OsStackProps
  extends Partial<Pick<MuiStackProps, "children" | "direction" | "divider">> {
  /**
   * Defines the space between immediate children.
   * Each unit represents 4px (GRID_SIZE).
   *
   * Can be a single number or an object with responsive breakpoints.
   *
   * @default 0
   *
   * @example
   * // Single value
   * spacing={2} // 8px (2 * 4px)
   *
   * @example
   * // Responsive values
   * spacing={{ xs: 1, sm: 2, md: 4 }} // 4px, 8px, 16px
   */
  spacing?: ResponsiveValue<number>;
  /**
   * Defines how children are distributed along the main axis.
   *
   * @default undefined
   *
   * @example
   * justifyContent="center" // Centers children horizontally (if direction="row") or vertically (if direction="column")
   * justifyContent="space-between" // Distributes children with space between them
   */
  justifyContent?: MuiStackProps["justifyContent"];
  /**
   * Defines how children are aligned along the cross axis.
   *
   * @default undefined
   *
   * @example
   * alignItems="center" // Centers children vertically (if direction="row") or horizontally (if direction="column")
   * alignItems="stretch" // Stretches children to fill the container
   */
  alignItems?: MuiStackProps["alignItems"];
  /**
   * Whether the Stack should take up the full width of its container.
   *
   * @default false
   */
  fullWidth?: boolean;
}

export const OsStack = React.forwardRef<HTMLDivElement, OsStackProps>(
  (props, ref) => {
    const {
      spacing,
      children,
      direction,
      divider,
      justifyContent,
      alignItems,
      fullWidth,
    } = props;
    return (
      <MuiStack
        ref={ref}
        spacing={multiplySpacing(spacing)}
        direction={direction}
        divider={divider}
        sx={{
          justifyContent,
          alignItems,
          width: fullWidth ? "100%" : undefined,
        }}
      >
        {children}
      </MuiStack>
    );
  },
);
